import SEO from "@/common/components/seo";
import FadeIn from "@/common/components/fade-in";
import MerchHeader from "../components/merch-header";
import ProductCard from "../components/product-card";
import useHttpProvider from "@/common/hooks/use-http";
import { useSiteSettings } from "@/common/providers/site-settings";
import { useEffect, useMemo, useState } from "react";
import { MerchProduct } from "../models/merch";
import * as StorefrontService from "../services/storefront";
import { toMerchProduct } from "../lib/storefront";
import { HomeRoute } from "@/features/home/route";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function MerchPage() {
	const { http } = useHttpProvider();
	const baseURL = import.meta.env.VITE_SERVER_API_URL;
	const { settings, isLoading: isSettingsLoading } = useSiteSettings();
	const storeEnabled = settings.storeEnabled;

	const [products, setProducts] = useState<MerchProduct[]>([]);
	const [isLoading, setIsLoading] = useState(true);

	const visibleProducts = useMemo(() => products, [products]);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			if (!storeEnabled) {
				setProducts([]);
				setIsLoading(false);
				return;
			}

			setIsLoading(true);
			try {
				const apiProducts = await StorefrontService.getProducts(http);
				if (cancelled) return;
				setProducts(apiProducts.map((p) => toMerchProduct(p, baseURL)));
			} catch {
				if (cancelled) return;
				setProducts([]);
			} finally {
				if (cancelled) return;
				setIsLoading(false);
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [http, baseURL, storeEnabled]);

	if (!isSettingsLoading && !storeEnabled) {
		return (
			<div className="min-h-screen bg-white">
				<SEO
					title="Store - Reel Rewards"
					description="The Reel Rewards store is currently unavailable."
				/>
				<FadeIn>
					<div className="flex flex-col items-center gap-6 px-4 py-20 text-center md:px-37">
						<p className="web-heading-h3">Store is currently unavailable</p>
						<p className="web-body-b1 text-gray-700">
							We’re doing some updates behind the scenes.
						</p>
						<div className="mt-2 flex w-full justify-center">
							<Button asChild className="w-full md:w-44">
								<Link to={HomeRoute}>Go Home</Link>
							</Button>
						</div>
					</div>
				</FadeIn>
			</div>
		);
	}

	return (
		<div>
			<SEO
				title="Store - Reel Rewards"
				description="Explore the Reel Rewards store, including apparel and gear built for the water."
			/>
			<MerchHeader
				title="Store"
				subtitle="Limited drops. Built for anglers. Designed for everyday."
			/>
			<FadeIn>
				<div className="flex flex-col gap-8 px-4 py-10 md:px-37 md:py-16">
					<div className="flex flex-col gap-4 text-center">
						<p className="web-heading-h2">
							Community Favorites,{" "}
							<span className="text-blue-500">Ready to Ship</span>
						</p>
						<p className="web-body-b1 text-gray-700">
							Choose your fit, pick your color, and gear up for the next trip.
						</p>
					</div>
					<div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3">
						{isLoading ? (
							<div className="col-span-full text-center">
								<p className="web-body-b1 text-gray-700">Loading products…</p>
							</div>
						) : visibleProducts.length === 0 ? (
							<div className="col-span-full text-center">
								<p className="web-body-b1 text-gray-700">
									No products are available right now.
								</p>
							</div>
						) : (
							visibleProducts.map((product) => (
								<ProductCard key={product.id} product={product} />
							))
						)}
					</div>
				</div>
			</FadeIn>
		</div>
	);
}
